/**
 * StatusBadge Component
 * Status pill for task status with color-coded styling.
 */

import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';
import type { TaskStatus } from '../../types/tasks';

export interface StatusBadgeProps {
  status: TaskStatus;
  size?: 'sm' | 'md';
}

interface StatusConfig {
  bg: string;
  color: string;
  label: string;
}

const STATUS_CONFIG: Record<TaskStatus, StatusConfig> = {
  // warning.bg is #fffbeb (lighter) — using Tailwind amber-100 for stronger pill contrast
  PENDING: { bg: theme.colors.status.warning.bg, color: '#92400e', label: 'Pending' },
  // neutral gray — theme.colors.backgrounds.gray matches #f3f4f6; text has no theme token
  BLOCKED: { bg: theme.colors.backgrounds.gray, color: '#6b7280', label: 'Blocked' },
  // success.bg is #f0fdf4 (lighter) — using Tailwind emerald-100 for stronger pill contrast
  AVAILABLE: { bg: '#d1fae5', color: theme.colors.status.success.text, label: 'Available' },
  // Blue (info-like) — not in the theme palette; keeping hex values
  IN_PROGRESS: { bg: '#dbeafe', color: '#1e40af', label: 'In Progress' },
  COMPLETED: { bg: '#d1fae5', color: theme.colors.status.success.text, label: 'Completed' },
  // neutral gray — same as BLOCKED
  SKIPPED: { bg: theme.colors.backgrounds.gray, color: '#6b7280', label: 'Skipped' },
  // error.bg is #fef2f2 (lighter) — using Tailwind red-100 for stronger pill contrast
  OVERDUE: { bg: '#fee2e2', color: '#991b1b', label: 'Overdue' },
};

const Pill = styled.span<{ $bg: string; $color: string; $size: 'sm' | 'md' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};

  ${({ $size }) =>
    $size === 'sm'
      ? css`
          padding: 2px 10px;
          font-size: 12px;
          line-height: 1.5;
        `
      : css`
          padding: 4px 12px;
          font-size: 13px;
          line-height: 1.5;
        `}
`;

export function StatusBadge({ status, size = 'sm' }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];

  return (
    <Pill $bg={config.bg} $color={config.color} $size={size} role="status" aria-label={`Status: ${config.label}`}>
      {config.label}
    </Pill>
  );
}

export default StatusBadge;
